import { Signpost, Ban, BellRing, IndianRupee, FilePenLine, Star, Smartphone, Handshake, MapPin, Timer, AlarmClock, CalendarCheck, Type } from "lucide-react";
import {
  PackageCheck, LayoutDashboard, Users, ClipboardList, FlaskConical, TestTube2, FileCheck2, FileText, Truck, UserCog, Route as RouteIcon, Wallet,
  Receipt, CalendarClock, TrendingUp, BadgePercent, Database, Beaker,
  Building2, Stethoscope, ShieldCheck, ScrollText,
  Settings2, KeyRound, AlertTriangle, Microscope, LocateFixed,
  Gift, Tags, Layers, Inbox, ShoppingCart, FileType2, Ticket, Image, BarChart3, PenTool, Calculator,
  Boxes, LifeBuoy, Landmark, Send, Barcode, Network, Hospital, Hourglass,
  Briefcase, Target,
} from "lucide-react";

// The console's menu, in the order of the access specification's Super Admin
// sidebar. Every item names the permission module (`mod`) that unlocks it:
// the sidebar shows an item only when the signed-in role owns that module
// (see Backend/src/config/rbac.js), and DashboardLayout refuses the page
// itself when the URL is typed by hand. Each section carries its own icon
// (shown on the section header, Atlas-style); item icons are used only when
// the sidebar is collapsed to a rail.
// The stages of a sample under Specimens, each shown to the roles that work it (the user's call,
// 2026-10-14): the collection side (desk, center, franchise, home collection) collects and sends;
// the lab side receives, accepts and runs. Rejected and All are for both. Super Admin sees all;
// a role in neither list sees all rather than none.
const SPECIMEN_COLLECT = ["RECEPTIONIST", "CENTER_ADMIN", "COLLECTION_CENTER", "FRANCHISE", "HOME_COLLECTION_COORDINATOR", "TECHNICIAN", "SENIOR_TECHNICIAN", "HUB_ADMIN", "OPERATIONS_COORDINATOR", "RUNNER"];
const SPECIMEN_LAB = ["TECHNICIAN", "SENIOR_TECHNICIAN", "PATHOLOGIST", "HUB_ADMIN", "HUB_RECEIVING", "QUALITY_MANAGER", "OPERATIONS_COORDINATOR"];
// Five tabs since 2026-10-27 (the user's list): Collected also holds the tubes on their way to the hub
// (batched, dispatched, in transit), so it is for both sides; Accepted also holds the tubes in work.
export const SPECIMEN_STAGES = [
  ["Pending", SPECIMEN_COLLECT], ["Collected", [...SPECIMEN_COLLECT, ...SPECIMEN_LAB]],
  ["Received", SPECIMEN_LAB], ["Accepted", SPECIMEN_LAB],
  ["Rejected", [...SPECIMEN_COLLECT, ...SPECIMEN_LAB]],
].map(([label, roles]) => ({ label: `${label} Samples`, roles, param: "stage", stage: label.toLowerCase(), path: `/app/specimens?stage=${label.toLowerCase()}`, testid: `nav-specimens-${label.toLowerCase()}` }));
// Today's Worklist and Lab Orders split into Walk In (everything but a B2B center's orders) and B2B
// (2026-10-27): ?channel=walkin|b2b on the same page. A B2B center's own login sees only its own orders,
// so it gets no split.
// "Walk In" is called Center Billing since 2026-10-30 (the value stays walkin)
const channels = (base, key) => [["Center Billing", "walkin"], ["B2B Billings", "b2b"]].map(([label, value]) => ({
  label, param: "channel", stage: value, path: `${base}?channel=${value}`, testid: `nav-${key}-${value}`,
  hideFor: (s) => !!s?.b2bPartnerId || s?.roleFamily === "B2B",
}));
// The sub-links this login may see, by its own role exactly: a role borrowing another's rights
// (Hub Receiving and Runner borrow Technician's) still sees only its own side.
export const stagesFor = (role, stages = SPECIMEN_STAGES) => { if (role === "SUPER_ADMIN") return stages; const mine = stages.filter((c) => !c.roles || c.roles.includes(role)); return mine.length ? mine : stages; };

export const NAV = [
  {
    section: null,
    items: [
      { label: "Dashboard", icon: LayoutDashboard, path: "/app/dashboard", testid: "nav-dashboard", mod: "home" },
      { label: "Today's Worklist", icon: ClipboardList, path: "/app/workspace", testid: "nav-workspace", mod: "worklist", children: channels("/app/workspace", "workspace") },
    ],
  },
  {
    section: "Orders & Billing",
    icon: ClipboardList,
    items: [
      { label: "Lab Orders", icon: ClipboardList, path: "/app/orders", testid: "nav-orders", mod: "orders", children: channels("/app/orders", "orders") },
      // A B2B center's login: every order with where its report stands, to download (2026-10-29); staff use Laboratory > Reports.
      { label: "Reports", icon: FileText, path: "/app/b2b-reports", testid: "nav-b2b-reports", mod: "reports", hideFor: (s) => !(s?.b2bPartnerId || s?.roleFamily === "B2B") },
      { label: "Order Edit Requests", icon: FilePenLine, path: "/app/order-edit-requests", testid: "nav-order-edits", mod: "orders",
        // not on a B2B center's login menu (user, 2026-10-29)
        hideFor: (s) => !!s?.b2bPartnerId || s?.roleFamily === "B2B" },
      { label: "Estimates", icon: Calculator, path: "/app/estimates", testid: "nav-estimates", mod: "estimates" },
      // Cancelled orders and orders with every test removed, kept out of Result Entry (2026-10-29); under Orders & Billing since 2026-10-30.
      { label: "Cancelled Orders", icon: Ban, path: "/app/cancelled-orders", testid: "nav-cancelled-orders", mod: "results" },
    ],
  },
  {
    section: "Laboratory",
    icon: FlaskConical,
    items: [
      // One menu item since 2026-10-29 (the user): the stages (SPECIMEN_STAGES) are tabs at the top of the page.
      { label: "Specimens", icon: TestTube2, path: "/app/specimens", testid: "nav-specimens", mod: "specimens" },
      // Off the menu since 2026-10-27 (user: "already there"): the Specimens tabs dispatch, receive,
      // reject and recollect. The pages stay for bag packing / hub scanning and the rejection
      // breakdown + CAPA; `hidden` keeps their permission check without drawing a link.
      { label: "Dispatch & Receiving", icon: PackageCheck, path: "/app/dispatch", testid: "nav-dispatch", mod: "specimens", hidden: true },
      { label: "Rejected Samples", icon: AlertTriangle, path: "/app/rejections", testid: "nav-rejections", mod: "rejections", hidden: true },
      // Laboratory tests only since 2026-10-29: scans are in the Scan Reporting section. The
      // Radiology Head works on scans only, so the lab's pages are not on its menu.
      { label: "Result Entry", icon: FlaskConical, path: "/app/results", testid: "nav-results", mod: "results", hideFor: (s) => s?.role === "RADIOLOGY_HEAD" },
      { label: "Pending Reports", icon: Hourglass, path: "/app/pending-reports", testid: "nav-pending-reports", mod: "pendingReports", hideFor: (s) => s?.role === "RADIOLOGY_HEAD" },
      { label: "Verify & Approve", icon: FileCheck2, path: "/app/verification", testid: "nav-verification", mod: "verification", hideFor: (s) => s?.role === "RADIOLOGY_HEAD" },
      { label: "Critical Values", icon: AlertTriangle, path: "/app/critical-values", testid: "nav-critical", mod: "critical" },
      { label: "Quality (IQC/EQAS)", icon: Microscope, path: "/app/quality", testid: "nav-quality", mod: "quality" },
      { label: "CAPA / NCR", icon: ShieldCheck, path: "/app/capa", testid: "nav-capa", mod: "capa" },
    ],
  },
  {
    // Scans and other non-laboratory tests on Novus orders (2026-10-29: "the radiology head is a
    // different person"): written by anyone who enters results, approved and dispatched by the
    // Radiology Head. Same pages as the lab's, scope "scan". Its own section, not under Radiology,
    // which is the partner centers' side; they report in their own portal (the user, 2026-10-29).
    section: "Scan Reporting",
    icon: FilePenLine,
    items: [
      { label: "Scan Result Entry", icon: FilePenLine, path: "/app/scan-results", testid: "nav-scan-results", mod: "results" },
      { label: "Pending Scan Reports", icon: Hourglass, path: "/app/scan-pending-reports", testid: "nav-scan-pending-reports", mod: "pendingReports" },
      { label: "Scan Verify & Approve", icon: FileCheck2, path: "/app/scan-verification", testid: "nav-scan-verification", mod: "verification" },
    ],
  },
  {
    // Releasing reports covers lab tests and scans alike, so it is its own section, after both
    // (the user, 2026-10-30: "it does not depend on the laboratory; a separate module where the
    // report is checked before release"). Users & Roles groups the two modules the same way.
    section: "Report Release",
    icon: FileText,
    items: [
      // A B2B center's login has its own Reports page (B2B section) and the order page.
      { label: "Reports", icon: FileText, path: "/app/reports", testid: "nav-reports", mod: "reports", hideFor: (s) => !!s?.b2bPartnerId || s?.roleFamily === "B2B" },
      // Paid printed reports: printed, dispatched / ready at the center, delivered (2026-10-19)
      { label: "Report Hard Copies", icon: PackageCheck, path: "/app/hard-copies", testid: "nav-hard-copies", mod: "hardCopies" },
    ],
  },
  {
    // Turnaround time stands on its own: it is measured for scans as well as lab tests, so it is
    // not a Laboratory page (the user, 2026-10-23). The late-report tickets sit under the
    // analysis, apart from the patients' complaints (2026-10-22).
    section: "Turnaround Time (TAT)",
    icon: Timer,
    items: [
      { label: "TAT Analysis", icon: Timer, path: "/app/tat-analysis", testid: "nav-tat-analysis", mod: "tat" },
      { label: "TAT Tickets", icon: AlarmClock, path: "/app/tat-tickets", testid: "nav-tat-tickets", mod: "tatTickets" },
    ],
  },
  {
    section: "Patients",
    icon: Users,
    items: [
      { label: "Registration", icon: Users, path: "/app/patients", testid: "nav-patients", mod: "patients" },
      { label: "Prescriptions", icon: FileText, path: "/app/prescriptions", testid: "nav-prescriptions", mod: "prescriptions" },
    ],
  },
  {
    // Everything about collecting at homes, the field logins included (their own
    // group until the regrouping of 2026-10-19).
    section: "Home Collection",
    icon: Truck,
    items: [
      { label: "Bookings", icon: Truck, path: "/app/home-collection", testid: "nav-hc", mod: "homecollection" },
      // Bookings nobody accepted after the offer rounds: a person assigns them here.
      { label: "Manual Assign", icon: BellRing, path: "/app/manual-dispatch", testid: "nav-manual-dispatch", mod: "homecollection" },
      { label: "Live Tracking", icon: LocateFixed, path: "/app/live-tracking", testid: "nav-live", mod: "tracking" },
      { label: "Vendors / Runner Boys", icon: UserCog, path: "/app/executives", testid: "nav-executives", mod: "executives" },
      { label: "Barcode Sticker Stock", icon: Barcode, path: "/app/qr-stock", testid: "nav-qr-stock", mod: "qrstock" },
    ],
  },
  {
    // What the customer sees in the app and on the website, in one place (the client's
    // ask, 2026-10-19): the catalogue and prices, the marketing, then the customers'
    // own enquiries, tickets, carts and orders.
    section: "App & Website",
    // Novus's own app and website are run from here; a B2B center's login has no business with them (2026-10-23).
    hideFor: (s) => !!s?.b2bPartnerId || s?.roleFamily === "B2B",
    icon: Smartphone,
    items: [
      { label: "City Catalogue", icon: MapPin, path: "/app/city-catalogue", testid: "nav-city-catalogue", mod: "tests" },
      { label: "Health Categories", icon: Tags, path: "/app/health-categories", testid: "nav-health-categories", mod: "tests" }, // the Super Admin edits; others see the list
      { label: "Offers & Rewards", icon: Gift, path: "/app/offers", testid: "nav-offers", mod: "offers" },
      { label: "Coupons", icon: Ticket, path: "/app/coupons", testid: "nav-coupons", mod: "coupons" },
      { label: "Banners & Sliders", icon: Image, path: "/app/banners", testid: "nav-banners", mod: "banners" },
      { label: "Popular on Home", icon: Star, path: "/app/home-popular", testid: "nav-home-popular", mod: "banners" },
      { label: "Push Notifications", icon: BellRing, path: "/app/push", testid: "nav-push", mod: "push" },
      { label: "App Releases", icon: Smartphone, path: "/app/app-releases", testid: "nav-app-releases", mod: "settings" },
    ],
  },
  {
    // The customers' side moved out of App & Website into its own section (the user, 2026-10-29):
    // enquiries, complaints, carts left behind and the orders booked online.
    section: "CRM",
    hideFor: (s) => !!s?.b2bPartnerId || s?.roleFamily === "B2B",
    icon: Inbox,
    items: [
      // The CRM team's own desk (2026-10-30): their dashboard, every order with filters, and a scan
      // booked at a partner center for a stuck customer (lab orders / home visits: New Order).
      { label: "CRM Dashboard", icon: LayoutDashboard, path: "/app/crm", testid: "nav-crm-dashboard", mod: "leads" },
      { label: "Orders", icon: ClipboardList, path: "/app/crm/orders", testid: "nav-crm-orders", mod: "orders" },
      { label: "Book a Scan", icon: CalendarClock, path: "/app/crm/book-scan", testid: "nav-crm-book-scan", mod: "orders" },
      { label: "Enquiries & Leads", icon: Inbox, path: "/app/enquiries", testid: "nav-enquiries", mod: "leads" },
      { label: "Complaints & Tickets", icon: Ticket, path: "/app/tickets", testid: "nav-tickets", mod: "tickets" },
      // App / website carts that did not become a booking - chased by the desk.
      { label: "Cart Activity", icon: ShoppingCart, path: "/app/carts", testid: "nav-carts", mod: "carts" },
      // Lab Orders already filtered to the two online sources (Order sources master: MOBILE_APP, WEBSITE).
      { label: "App & Website Orders", icon: ClipboardList, path: "/app/orders?source=MOBILE_APP,WEBSITE", testid: "nav-app-orders", mod: "orders" },
    ],
  },
  {
    // The B2B channel in one place (the user's ask, 2026-10-22): the partner centers that book
    // orders themselves, their orders (the center's own booking or the desk's for it) and the
    // runner pickups that fetch their samples. B2B Orders is Lab Orders narrowed to B2B centers,
    // with a New B2B Order button that opens the order form in B2B mode (?mode=b2b).
    section: "B2B",
    icon: Building2,
    items: [
      { label: "B2B Centers", icon: Building2, path: "/app/b2b-centres", testid: "nav-b2b-centres", mod: "b2bCentres" },
      { label: "B2B Patients", icon: Users, path: "/app/b2b-patients", testid: "nav-b2b-patients", mod: "b2bPatients" }, // Patient Registration narrowed to the B2B centers' patients
      { label: "B2B Orders", icon: ClipboardList, path: "/app/b2b-orders", testid: "nav-b2b-orders", mod: "b2bOrders" },
      // Monthly tax invoices + per-order bills, to download and share (2026-10-24); a center login sees its own.
      { label: "B2B Invoices", icon: Receipt, path: "/app/b2b-invoices", testid: "nav-b2b-invoices", mod: "b2bInvoices" },
      // Center-wise orders, receipts and dues by day / month / range, with bill and test CSVs (2026-10-30)
      { label: "B2B Business Report", icon: BarChart3, path: "/app/b2b-business-report", testid: "nav-b2b-business-report", mod: "b2bCentres", hideFor: (s) => !!s?.b2bPartnerId || s?.roleFamily === "B2B" },
      { label: "Runner Pickups", icon: Truck, path: "/app/pickups", testid: "nav-pickups", mod: "pickups" },
    ],
  },
  {
    // The radiology channel in one place, like B2B (the user, 2026-10-22): own and partner scan
    // centers, the app's prepaid bookings, settlements, refunds, reports and outbound referrals.
    // Each item is one page of the radiology module (/app/radiology/<tab>, pages/Radiology.jsx).
    // Center Portal is a center login's own console; a Novus admin opens a center's portal from
    // the center's "View Center Portal" button. Anyone who owns the radiology module is sent
    // from /app/centre to Radiology Centers anyway (CentrePortal.jsx), so the Super Admin, who
    // owns every module, must not get the same page twice: `unless` hides the item for them
    // (the user saw two Hospital buttons, 2026-10-23).
    section: "Radiology",
    icon: Hospital,
    items: [
      // `also`: a center's portal opened from this page (/app/centre?partnerId=) keeps this item lit.
      { label: "Radiology Centers", icon: Hospital, path: "/app/radiology", testid: "nav-radiology", mod: "radiology", also: ["/app/centre"] },
      { label: "Scan Bookings", icon: CalendarClock, path: "/app/radiology/bookings", testid: "nav-radiology-bookings", mod: "radiology" },
      { label: "Center Settlements", icon: IndianRupee, path: "/app/radiology/settlements", testid: "nav-radiology-settlements", mod: "radiology" },
      { label: "Booking Refunds", icon: Receipt, path: "/app/radiology/refunds", testid: "nav-radiology-refunds", mod: "radiology" },
      { label: "Scan Reports", icon: FileText, path: "/app/radiology/reports", testid: "nav-radiology-reports", mod: "radiology" },
      { label: "Scan Referrals", icon: Send, path: "/app/radiology/referrals", testid: "nav-radiology-referrals", mod: "radiology" },
    ],
  },
  {
    // A radiology center login's own console (pages/centre/CentrePortal.jsx, CENTRE_PAGES): each
    // part is its own page since 2026-10-26 (the in-page tab strip did not line up). Hidden from
    // radiology module owners (Novus staff open a center's portal from Radiology Centers instead).
    section: "Center Portal",
    icon: Hospital,
    items: [
      { label: "Dashboard", icon: LayoutDashboard, path: "/app/centre", testid: "nav-centre-portal", mod: "centrePortal", access: ["radiology"], unless: ["radiology"] },
      { label: "Orders & Reports", icon: FileText, path: "/app/centre/orders", testid: "nav-centre-orders", mod: "centrePortal", access: ["radiology"], unless: ["radiology"] },
      { label: "Appointments", icon: CalendarCheck, path: "/app/centre/appointments", testid: "nav-centre-appointments", mod: "centrePortal", access: ["radiology"], unless: ["radiology"] },
      { label: "Payments", icon: Wallet, path: "/app/centre/payments", testid: "nav-centre-payments", mod: "centrePortal", access: ["radiology"], unless: ["radiology"] },
      { label: "E-Signatures", icon: PenTool, path: "/app/centre/signatures", testid: "nav-centre-signatures", mod: "centrePortal", access: ["radiology"], unless: ["radiology"] },
      { label: "Manage Slots", icon: CalendarClock, path: "/app/centre/slots", testid: "nav-centre-slots", mod: "centrePortal", access: ["radiology"], unless: ["radiology"] },
      { label: "Manage Pricing", icon: Tags, path: "/app/centre/prices", testid: "nav-centre-prices", mod: "centrePortal", access: ["radiology"], unless: ["radiology"] },
      { label: "Profile & Facilities", icon: Building2, path: "/app/centre/profile", testid: "nav-centre-profile", mod: "centrePortal", access: ["radiology"], unless: ["radiology"] },
      { label: "Statistics", icon: BarChart3, path: "/app/centre/statistics", testid: "nav-centre-statistics", mod: "centrePortal", access: ["radiology"], unless: ["radiology"] },
    ],
  },
  {
    // Outside parties Novus works with.
    section: "Partners",
    icon: Handshake,
    items: [
      // Reference Centers removed (2026-10-27): every center is a B2B center (B2B section), a login optional
      // Franchise Centers removed from the menu (the user, 2026-10-29): a franchise is added and given its login on
      // Master Data → Cities, Hubs & Own Centers (type Franchise), its commission settled under Finance → Commissions.
      { label: "Outsource Labs", icon: Send, path: "/app/outsourcing", testid: "nav-outsourcing", mod: "outsourcing" },
      { label: "Doctors", icon: Stethoscope, path: "/app/doctors", testid: "nav-doctors", mod: "doctors" },
      // hospitals / clinics / labs that refer patients the way a doctor does (2026-10-29)
      { label: "Referring Centers", icon: Hospital, path: "/app/referring-centres", testid: "nav-referring-centres", mod: "doctors" },
    ],
  },
  {
    section: "Finance & Accounts",
    icon: Landmark,
    items: [
      { label: "Payments", icon: Wallet, path: "/app/payments", testid: "nav-payments", mod: "payments" },
      // not in a B2B center login's menu (the user, 2026-10-29): its payments are under Payments
      { label: "Receipts", icon: IndianRupee, path: "/app/receipts", testid: "nav-receipts", mod: "receipts", hideFor: (s) => !!s?.b2bPartnerId || s?.roleFamily === "B2B" },
      { label: "Expenses", icon: Receipt, path: "/app/expenses", testid: "nav-expenses", mod: "expenses" },
      { label: "Day Closing", icon: CalendarClock, path: "/app/day-closing", testid: "nav-day-closing", mod: "dayclosing" },
      { label: "Commissions", icon: BadgePercent, path: "/app/commissions", testid: "nav-commissions", mod: "commissions" },
      { label: "Wallet & Referrals", icon: Gift, path: "/app/wallet", testid: "nav-wallet", mod: "wallet" },
    ],
  },
  {
    section: "Inventory",
    icon: Boxes,
    items: [
      { label: "Stock & Purchase", icon: Boxes, path: "/app/inventory", testid: "nav-inventory", mod: "inventory" },
      // Lab technicians and collection center admins ask for items; Purchase / Hub Admin approve and issue (2026-11-02)
      { label: "Item Requests", icon: PackageCheck, path: "/app/item-requests", testid: "nav-item-requests", mod: "itemRequests" },
    ],
  },
  {
    section: "Users & Access",
    icon: KeyRound,
    items: [
      { label: "Users & Roles", icon: KeyRound, path: "/app/users", testid: "nav-users", mod: "users" },
      { label: "Approval Settings", icon: PenTool, path: "/app/approval-settings", testid: "nav-approval-settings", mod: "approvals" },
    ],
  },
  {
    // The lab's own set-up. Partners, coupons and the app's catalogue moved out on 2026-10-19.
    section: "Master Data",
    icon: Database,
    items: [
      { label: "Business Units", icon: Network, path: "/app/business-units", testid: "nav-business-units", mod: "businessUnits" },
      { label: "Cities, Hubs & Own Centers", icon: Beaker, path: "/app/centers", testid: "nav-centers", mod: "centers" },
      { label: "Tests & Packages", icon: Database, path: "/app/tests", testid: "nav-tests", mod: "tests" },
      { label: "Departments", icon: Layers, path: "/app/departments", testid: "nav-departments", mod: "departments" },
      // The New Order form's Source list: rename / switch off / delete, a deleted source's orders moved (2026-11-02)
      { label: "Order Sources", icon: Signpost, path: "/app/order-sources", testid: "nav-order-sources", mod: "centers", hideFor: (s) => s?.role !== "SUPER_ADMIN" },
      // Off the menu for Novus staff (the user, 2026-10-29: "no need"; prices are set on the City Catalogue,
      // the centers' and B2B centers' price lists). A collection-center / franchise login keeps it: there
      // it is "Prices at <its center>". The page stays reachable at /app/pricing.
      { label: "Price Lists", icon: Tags, path: "/app/pricing", testid: "nav-pricing", mod: "pricing", hideFor: (s) => !(s?.scope?.type === "CENTER" && s?.centerId) },
      { label: "Report Layouts", icon: FileType2, path: "/app/report-templates", testid: "nav-report-templates", mod: "reportTemplates" },
      // Font sizes, signature size and spacing of the report PDF (2026-10-26).
      { label: "Report Print Style", icon: Type, path: "/app/report-style", testid: "nav-report-style", mod: "reportTemplates" },
      { label: "E-Signature", icon: PenTool, path: "/app/signatures", testid: "nav-signatures", mod: "signatures" },
      // Home-collection setup moved here from Home Collection (the user, 2026-10-30): service areas, slots, charges.
      { label: "Areas", icon: RouteIcon, path: "/app/zones", testid: "nav-zones", mod: "zones" },
      { label: "Collection Slots", icon: CalendarClock, path: "/app/home-collection-slots", testid: "nav-home-slots", mod: "zones" },
      { label: "Collection Charges", icon: IndianRupee, path: "/app/collection-charges", testid: "nav-collection-charges", mod: "homecollection" },
    ],
  },
  {
    // Internal Marketing CRM (2026-10-19): business-development leads only - doctors, B2B,
    // corporate, apartments, franchise - worked by marketing executives. Patient enquiries
    // stay under CRM / Marketing → Enquiries.
    section: "Marketing Team", // "Marketing CRM" until 2026-10-30 (the user: "Marketing team, not CRM")
    icon: Briefcase,
    items: [
      { label: "Dashboard", icon: LayoutDashboard, path: "/app/marketing/dashboard", testid: "nav-mkt-dashboard", mod: "mktDashboard" },
      { label: "Marketing Executives", icon: UserCog, path: "/app/marketing/executives", testid: "nav-mkt-executives", mod: "mktExecutives" },
      { label: "Marketing Leads", icon: Target, path: "/app/marketing/leads", testid: "nav-mkt-leads", mod: "mktLeads" },
      { label: "Activities & Follow-ups", icon: CalendarClock, path: "/app/marketing/activities", testid: "nav-mkt-activities", mod: "mktActivities" },
      { label: "Partner Onboarding", icon: Handshake, path: "/app/marketing/onboarding", testid: "nav-mkt-onboarding", mod: "mktOnboarding" },
      { label: "Business & Revenue", icon: IndianRupee, path: "/app/marketing/revenue", testid: "nav-mkt-revenue", mod: "mktRevenue" },
      { label: "Reports", icon: BarChart3, path: "/app/marketing/reports", testid: "nav-mkt-reports", mod: "mktReports" },
    ],
  },
  {
    section: "Reports / Analytics",
    icon: BarChart3,
    items: [
      { label: "Analytics", icon: TrendingUp, path: "/app/analytics", testid: "nav-analytics", mod: "analytics" },
      { label: "MIS & Statistics", icon: BarChart3, path: "/app/statistics", testid: "nav-statistics", mod: "statistics" },
      { label: "Lab Order Report", icon: ClipboardList, path: "/app/statistics?report=lab-orders", testid: "nav-lab-order-report", mod: "statistics" },
    ],
  },
  {
    section: "Audit & Settings",
    icon: Settings2,
    items: [
      { label: "Audit Trail", icon: ScrollText, path: "/app/audit", testid: "nav-audit", mod: "audit" },
      { label: "Settings", icon: Settings2, path: "/app/settings", testid: "nav-settings", mod: "settings" },
    ],
  },
  {
    section: "Help & Support",
    icon: LifeBuoy,
    items: [
      { label: "My Access & Support", icon: LifeBuoy, path: "/app/help", testid: "nav-help", mod: "help" },
    ],
  },
];

export const NAV_ITEMS = NAV.flatMap((g) => g.items);

// The menu item a URL belongs to (/app/orders/new and /app/orders/:id both
// belong to Lab Orders; /app/tests/CBC to Tests & Packages).
export function findNavItem(pathname) {
  let best = null;
  for (const item of NAV_ITEMS) {
    if (pathname === item.path || pathname.startsWith(item.path + "/")) {
      if (!best || item.path.length > best.path.length) best = item;
    }
  }
  return best;
}

// The menu filtered to what a permission check allows; empty sections vanish.
// An item's `unless` names modules whose owners are better served elsewhere
// (Center Portal for a login that already has Radiology Centers): it is not drawn for them.
// hideFor(session) on a section or an item keeps it off a login's menu whatever modules the
// role owns (the App & Website section for a B2B center's login).
export function navFor(allowed, session) {
  const shown = (i) => !i.hidden && allowed(i.mod) && !(i.unless || []).some((m) => allowed(m)) && !(i.hideFor && session && i.hideFor(session));
  return NAV.filter((g) => !(g.hideFor && session && g.hideFor(session))).map((g) => ({ ...g, items: g.items.filter(shown) })).filter((g) => g.items.length);
}

// May this permission check open the page behind a menu item? The menu draws
// an item only for roles that own its `mod`; `access` adds modules whose owners
// may still open the page when they arrive by link.
export function canOpen(item, allowed) {
  return !item || allowed(item.mod) || (item.access || []).some((m) => allowed(m));
}

// Where a login lands. Every Novus role owns the dashboard (`home`); a role that
// does not (a partner radiology centre) lands on the first page of its own menu.
// Until the permissions are known the dashboard stays the answer, as before.
const DASHBOARD_PATH = "/app/dashboard";
export function homePathFor(session) {
  if (!session || session.role === "SUPER_ADMIN" || !session.permissions) return DASHBOARD_PATH;
  const owns = (mod) => !!session.permissions[mod]?.length;
  if (owns("home")) return DASHBOARD_PATH;
  const first = NAV_ITEMS.find((i) => owns(i.mod));
  return first ? first.path : "/app/help";
}
